/* @flow */
import {
  RECEIVE_FOUNDRY_RESPONSE,
  RECEIVE_MORE_CONTENT_FOUNDRY_RESPONSE
} from '../actions/foundry_response';
import {
  ARTICLE_SLIDESHOW_PAGE_TYPE,
  CARD_TYPE_PRODUCTS,
  CARD_TYPE_CHANNEL,
  CARD_TYPE_SERIES,
  CARD_TYPE_CTA_BUTTON,
  ARCHIVES_PAGE_TYPE,
  CHANNEL_PAGE_TYPE,
  PRODUCT_DETAIL_PAGE_TYPE,
  PRODUCT_LIST_PAGE_TYPE,
  SEARCH_PAGE_TYPE,
  VIDEO_SERIES_PAGE_TYPE,
  CARD_TYPE_HTML,
  CARD_TYPE_SHOPS_CATEGORY,
  WATCH_HOME_IDS,
  CARD_TYPE_POPULAR_ENTRY,
  US_HOMEPAGE_ID,
  CARD_TYPE_SUPER_SALE
} from '../constants';
import {
  moduleAdsCadence
} from '../utils/ads/cadences';
import {
  channelAdPositionGenerator,
  relatedContentAdPositionGenerator
} from '../utils/ads/positionStrings';
import {
  FEATURED_STORY_MODULE_DATA as featuredStory
} from '../components/module/featured_story_module_data';
import {
  SUPER_SALE_MODULE_DATA as superSale
} from '../components/module/super_sale_data';
import extract from '../utils/extract';
import {get, find, last, omit, take, omitBy, filter, reduce, compact, isEmpty, toLower, findIndex, chunk, flatMap} from 'lodash';

import type { Action } from '../actions/types';
import type { Field } from '../utils/extract';

type CardAttributes = Field[];

const CARD_ATTRIBUTES: CardAttributes = [
  'id',
  'type',
  'title',
  'truncated_title_80',
  'truncated_title_62',
  'hero_shorter_title',
  'published',
  'asset_main.src_full',
  'excerpt_truncated_155',
  'label',
  ['contributors', ['display_name']],
  ['opener_images', ['type', 'asset.src', 'alt_title']],
  'meta.sold_content_type',
  'url',
  'path',
  'display_name',
  'entries',
  'episode_count',
  'is_video_card',
  'blog_series',
  'series_name'
];

const CARD_ATTRIBUTES_PRODUCT: CardAttributes = [
  'id',
  'is_sponsored',
  'is_video_card',
  'click_tracker',
  'impression_tracker',
  'offer',
  'name',
  'short_name',
  'brand',
  'category',
  'product_url',
  'path',
  'thumbnail_url',
  'is_sem3_available',
  'images'
];

const CARD_ATTRIBUTES_MINIMAL: CardAttributes = [
  /*
   * These attributes are shared by CHANNEL and SERIES type cards,
   * which have similarly pared-down designs.
   */
  'id',
  'name',
  'url',
  'opener_images'
];

const CARD_ATTRIBUTES_CTA_BUTTON: CardAttributes = [
  'id',
  'label',
  'prompt',
  'url',
  'src_full'
];

const CARD_ATTRIBUTES_HTML: CardAttributes = [
  'id',
  'content'
];

const CARD_ATTRIBUTES_POPULAR_ENTRY: CardAttributes = [
  'opener_images',
  'title',
  'url',
  'url_full',
  'id',
  'category'
];

const CARD_ATTRIBUTES_SUPER_SALE: CardAttributes = [
  'bylines',
  'product',
  'display_date',
  'id'
];

const CARD_ATTRIBUTES_CHANNEL = CARD_ATTRIBUTES_MINIMAL;
const CARD_ATTRIBUTES_SERIES = [...CARD_ATTRIBUTES_MINIMAL, 'genre', 'tagline'];
const CARD_ATTRIBUTES_SHOPS_CATEGORY = [...CARD_ATTRIBUTES_MINIMAL, 'tagline'];

const INITIAL_STATE = [];

/*
 * Reducer
 */

export default
(state: ModulesState = INITIAL_STATE, action: Action): ModulesState => {
  switch (action.type) {
    case RECEIVE_FOUNDRY_RESPONSE:
      return shimModulesState(state, action);

    case RECEIVE_MORE_CONTENT_FOUNDRY_RESPONSE:
      const numModules = state.length;
      if (numModules === 0) {
        return shimModulesState(state, action);
      }
      /*
       * Case:   The last module in the state is the Feed module
       * Result: Concatenate the rows of the incoming Feed module to the
       *         Feed module currently stored in state, then re-shim ads.
       */
      const lastModule = state[numModules - 1];
      if (lastModule.type === 'feed') {
        const oldModules = take(state, numModules - 1);
        const newFeedRows = getNewFeedRows(state, action);
        const feedModule = {
          ...lastModule,
          rows: [
            ...lastModule.rows,
            ...newFeedRows
          ]
        };
        /*
         * We've added more rows to the feed module so we must now recalculate
         * the placement of the ads within the Feed module by calling shimAds()
         */
        return shimAds([...oldModules, feedModule], action.data);
      }

      /*
       * Case:   The last module is not a feed module
       * Result: Concatenate the new modules to the old modules
       */
      return [
        ...state,
        ...shimModulesState(state, action)
      ];

    default:
      return state;
  }
};

/*
 * Shim helpers
 */

const isSponsoredModule = (module: Object): bool =>
  get(module, 'has_ad_sponsor');

const onWatchPage = (data: Object): bool =>
  toLower(get(data, 'page_data.page_type')) === CHANNEL_PAGE_TYPE
    && WATCH_HOME_IDS.includes(get(data, 'page_data.type_id'));

const onSlideshowPage = (data: Object): bool =>
  get(data, 'page_data.page_type') === ARTICLE_SLIDESHOW_PAGE_TYPE;

const onEpisodePage = (data: Object): bool =>
  get(data, 'page_data.page_type') === VIDEO_SERIES_PAGE_TYPE;

const onShoppingPage = (data: Object): bool =>
  get(data, 'page_data.page_type') === PRODUCT_DETAIL_PAGE_TYPE;

const onProductListPage = (data: Object): bool =>
  get(data, 'page_data.page_type') === PRODUCT_LIST_PAGE_TYPE;

const onAggregationPage = (data: Object): bool =>
  [
    ARCHIVES_PAGE_TYPE,
    CHANNEL_PAGE_TYPE,
    SEARCH_PAGE_TYPE
  ].includes(toLower(get(data, 'page_data.page_type')));

const shouldDisplayModuleTitle =
  (module: Object, moduleIndex: number, { isAggregationPage, firstCollectionModuleIndex, basename }: ShimOptions): bool =>
    !isAggregationPage
      || module.type === 'page-header'
      || (moduleIndex > firstCollectionModuleIndex && firstCollectionModuleIndex !== -1)
      || basename === 'archives';

const getNonEmptyRows = (rows: Array<Object>): Array<Object> =>
  filter(rows, (row: ?Object): bool => !isEmpty(get(row, 'entities')));

const getNonEmptyEntities = (entities: Array<Object>): Array<Object> =>
  filter(entities, (entity: Object): bool => !isEmpty(get(entity, 'object')));

const getModuleCounts = (state: ModulesState): ModuleCounts => {
  const lastModule = last(state);
  const lastRow = last(get(lastModule, 'rows'));
  const lastCard = last(get(lastRow, 'cards'));
  return {
    cardCount: get(lastCard, 'cardID', 0),
    rowCount: get(lastRow, 'rowID', 0),
    cardCountWithinModule: get(lastCard, 'indexWithinModule'),
    rowCountWithinModule: get(lastRow, 'indexWithinModule')
  };
};

const isVideoCard = (card: Object): bool =>
  get(card, 'is_video_card') || false;

const getCardAttributes = (type: string): CardAttributes => {
  switch (type) {
    case CARD_TYPE_PRODUCTS: return CARD_ATTRIBUTES_PRODUCT;
    case CARD_TYPE_CHANNEL: return CARD_ATTRIBUTES_CHANNEL;
    case CARD_TYPE_SERIES: return CARD_ATTRIBUTES_SERIES;
    case CARD_TYPE_CTA_BUTTON: return CARD_ATTRIBUTES_CTA_BUTTON;
    case CARD_TYPE_HTML: return CARD_ATTRIBUTES_HTML;
    case CARD_TYPE_SHOPS_CATEGORY: return CARD_ATTRIBUTES_SHOPS_CATEGORY;
    case CARD_TYPE_POPULAR_ENTRY: return CARD_ATTRIBUTES_POPULAR_ENTRY;
    case CARD_TYPE_SUPER_SALE: return CARD_ATTRIBUTES_SUPER_SALE;
    default: return CARD_ATTRIBUTES;
  }
};

const getCardPropsFromEntityType =
  ({ object, type }: Object): Object => extract(
    object,
    getCardAttributes(type)
  );

/*
 * Shims
 */

const shimEntities =
  (entities: Array<Object>, { module, ...options }: Object): CardsAccumulator =>
    reduce(entities, ({ cards, cardCount, cardCountWithinModule }: CardsAccumulator, entity: Object, index: number): CardsAccumulator => ({
      cardCount: cardCount + 1,
      cardCountWithinModule: cardCountWithinModule + 1,
      cards: [
        ...cards,
        {
          cardID: cardCount + 1,
          moduleID: module.id + 1,
          indexWithinRow: index + 1,
          indexWithinModule: cardCountWithinModule + 1,
          isVideoCard: isVideoCard(entity.object),
          ...getCardPropsFromEntityType(entity)
        }
      ]
    }),
    { cards: [], ...options });

const shimRows = (rows: Array<Object>, { module, ...options }: Object): RowsAccumulator =>
  reduce(rows, ({ rows, cardCount, rowCount, cardCountWithinModule = 0, rowCountWithinModule = 0 }: RowsAccumulator, row: Object): RowsAccumulator => {
    const { cards, ...newCounts } = shimEntities(
      getNonEmptyEntities(row.entities),
      { ...options, row, module, cardCount, cardCountWithinModule }
    );
    return {
      cardCount: newCounts.cardCount,
      rowCount: rowCount + 1,
      cardCountWithinModule: newCounts.cardCountWithinModule,
      rowCountWithinModule: rowCountWithinModule + 1,
      rows: [
        ...rows,
        {
          ...omit(row, 'entities'),
          cards: cards,
          rowID: rowCount + 1,
          indexWithinModule: rowCountWithinModule + 1
        }
      ]
    };
  },
  { rows: [], ...options });

const shimModules = (modules: Array<Object>, options: Object): ModulesAccumulator =>
  reduce(modules, ({ modules, cardCount, rowCount }: ModulesAccumulator, module: Object, i: number): ModulesAccumulator => {
    const { rows, ...newCounts } = shimRows(
      getNonEmptyRows(module.rows),
      { ...options, module, rowCount, cardCount }
    );
    return {
      cardCount: newCounts.cardCount,
      rowCount: newCounts.rowCount,
      modules: [
        ...modules,
        {
          ...module,
          rows,
          channelCampaignBrandingImage: options.channelCampaignBrandingImage || null,
          channelSubtitle: options.channelSubtitle || null,
          shouldDisplayModuleTitle: shouldDisplayModuleTitle(module, i, options)
        }
      ]
    };
  },
  { ...options, modules: [] });

const shimAds = (modules: Array<Object>, data: Object): Array<Object> => {
  const adCadence = moduleAdsCadence(modules);
  const generators = onAggregationPage(data)
    ? {
      desktop: channelAdPositionGenerator(),
      tablet: channelAdPositionGenerator(),
      mobileRows: channelAdPositionGenerator(),
      mobileCards: channelAdPositionGenerator()
    } : {
      desktop: relatedContentAdPositionGenerator(),
      tablet: relatedContentAdPositionGenerator(),
      mobileRows: relatedContentAdPositionGenerator(),
      mobileCards: relatedContentAdPositionGenerator()
    };

  return modules.map((module: Object, moduleIndex: number): Object => {
    if (!module.rows) {
      return module;
    }

    /*
     * Module ads will NOT show up AT ALL in:
     *   - sponsored modules
     *   - watch pages
     *   - series episode articles
     *   - shopping pages
     *   - shopping product-list pages
     */
    const shouldRenderModuleAds = (
      !isSponsoredModule(module)
      && !onWatchPage(data)
      && !onEpisodePage(data)
      && !onShoppingPage(data)
      && !onProductListPage(data)
      && ![
        'hp_refresh_feed',
        'hp_refresh_featured_story',
        'hp_refresh_trending_videos'
      ].includes(module.type)
    );

    /*
     * Module ads will NOT show up ON DESKTOP in:
     *  - slideshow articles
     */
    const shouldRenderDesktopModuleAds = !onSlideshowPage(data);

    /*
     * Q: Why is `mobileAdPosition` on both the row and card level?
     * A: If you look at our render logic on mobile we sometimes convert
     * individual cards into rows and we sometimes don't depending on the
     * module type. Putting `mobileAdPosition` on both levels accounts for
     * both use cases.
     */

    return {
      ...module,
      rows: module.rows.map((row: Object, rowIndex: number): Object => ({
        ...row,
        desktopAdPosition: (
          adCadence[moduleIndex].rows[rowIndex].hasDesktopAd
          && shouldRenderModuleAds
          && shouldRenderDesktopModuleAds
            ? generators.desktop.next().value
            : null
        ),
        tabletAdPosition: (
          adCadence[moduleIndex].rows[rowIndex].hasTabletAd
          && shouldRenderModuleAds
            ? generators.tablet.next().value
            : null
        ),
        mobileAdPosition: (
          adCadence[moduleIndex].rows[rowIndex].hasMobileAd
          && shouldRenderModuleAds
            ? generators.mobileRows.next().value
            : null
        ),
        cards: row.cards.map((card: Object, cardIndex: number): Object => ({
          ...card,
          mobileAdPosition: (
            adCadence[moduleIndex].rows[rowIndex].cards[cardIndex].hasMobileAd
            && shouldRenderModuleAds
              ? generators.mobileCards.next().value
              : null
          )
        }))
      }))
    };
  });
};

function getNewFeedRows(state: ModulesState, { data }: Object): Array<Object> {
  const newFeedModule = find(get(data, 'content.modules'), { type: 'feed' });
  if (!Array.isArray(newFeedModule.rows)) {
    return [];
  }
  const nonEmptyRows = getNonEmptyRows(newFeedModule.rows);
  const module = last(state);
  const { rows } = shimRows(nonEmptyRows, {
    module,
    ...getModuleCounts(state)
  });
  return rows;
}

function getHomepageRefreshModules(modules: ModulesState, data: Object): ModulesState {
  let curatedCollections;
  let trendingVideos;
  let mostPopular;

  const feedModule = find(modules, { type: 'feed' });
  const shoppingModule = find(modules, { type: 'shops_category' });
  const videoModule = find(modules, { type: 'blog_series' });
  const trendingModule = find(modules, { type: 'content' });
  const nonFeedModules = filter(modules, ({ type }: Object): bool => type !== 'feed' && type !== 'blog_series');
  const is_homepage = get(data, 'page_data.is_homepage');

  if (!feedModule
    || !is_homepage
    || isEmpty(get(feedModule, 'rows'))
  ) {
    return modules;
  }

  // leave until we add all editions
  if (get(feedModule, 'page_id') === US_HOMEPAGE_ID && trendingModule) {
    const superSaleIndex = trendingModule.display_order - 1;
    nonFeedModules.splice(superSaleIndex, 0, superSale);
  }

  // need this for mobile homepage refresh
  if (shoppingModule && shoppingModule.rows.length === 2) {
    const [featuredCategoryRow, flexCategoryRow] = shoppingModule.rows;
    curatedCollections = {
      ...shoppingModule,
      title: 'Shop R29 Favorites',
      type: 'hp_refresh_shops_category',
      rows: [{
        ...flexCategoryRow,
        entities: [
          ...featuredCategoryRow.entities,
          ...flexCategoryRow.entities
        ]
      }]
    };
  }

  const mostPopularData = get(data, 'page_data.most_popular', []);
  if (trendingModule && !isEmpty(mostPopularData) && mostPopularData.length > 2) {
    mostPopular = {
      ...trendingModule,
      title: 'Most Popular',
      type: 'hp_refresh_most_popular',
      rows: [{
        ...trendingModule.rows[0],
        type: 'most-popular',
        entities: mostPopularData.map((item: Object, index: number): Object => ({
          type: CARD_TYPE_POPULAR_ENTRY,
          object: {
            id: index,
            ...item
          }
        }))
      }]
    };
  }

  let editorsPick;
  if (get(feedModule, 'page_id') === US_HOMEPAGE_ID) {
    editorsPick = featuredStory;
  }


  /*
   * The nine entities from feed rows 3, 4, and 5 need to be restructured from 3 rows of 3 stories
   * to 2 rows of 4 stories and 1 row of 1 story.
   *
   * todo:
   *
   * - add isHomepage to API
   *
   * - trendingVideos???
   * - make sure original stories has a good placement
   * - create shopping module for non us and make sure it works
   */
  const feedRows3To5 = feedModule.rows.slice(2, 5);
  const feedEntities = chunk(flatMap(feedRows3To5, ({ entities }: Object): Array<Object> => entities), 4);
  const newFeedRows = feedRows3To5.map((row: Object, i: number): Object =>
    ({ ...row, entities: feedEntities[i] }));

  return [
    ...nonFeedModules,
    {
      ...feedModule,
      title: 'Now on R29',
      type: 'hp_refresh_feed',
      rows: feedModule.rows.slice(0, 2)
    },
    mostPopular,
    {
      ...feedModule,
      title: '',
      type: 'hp_refresh_feed_ads',
      rows: newFeedRows.slice(0, 1)
    },
    trendingVideos,
    editorsPick,
    {
      ...feedModule,
      title: '',
      type: 'hp_refresh_feed',
      rows: newFeedRows.slice(1, 2)
    },
    curatedCollections,
    {
      ...feedModule,
      title: '',
      type: 'hp_refresh_feed_ads',
      rows: [
        newFeedRows[2],
        ...feedModule.rows.slice(5, 10)
      ]
    },
    videoModule,
    {
      ...feedModule,
      title: '',
      type: 'hp_refresh_feed_ads',
      rows: feedModule.rows.slice(10, 14)
    },
    feedModule
  ].filter(Boolean);
}

function shimModulesState(state: ModulesState, { data, type }: Object): ModulesState {
  const newModulesFromAPI = get(data, 'content.modules');
  if (!Array.isArray(newModulesFromAPI)) {
    return state;
  }
  const stateHasPageHeaderModule = !!find(state, { type: 'page-header' });
  const compactModules = type === RECEIVE_FOUNDRY_RESPONSE
    ? compact(getHomepageRefreshModules(newModulesFromAPI, data))
    : compact(newModulesFromAPI);
  const modulesForShim = stateHasPageHeaderModule
    ? omitBy(compactModules, { type: 'page-header' })
    : compactModules;
  const basename = get(data, 'ad_data.ad_pageid');
  const firstCollectionModuleIndex = findIndex(
    modulesForShim,
    (module: Object): bool => module.type !== 'page-header'
  );
  const pageHeader = find(modulesForShim, { type: 'page-header' });
  const channelCampaignBrandingImage = get(pageHeader, 'campaign_branding_image_mobile_url');
  const channelSubtitle = get(pageHeader, 'subtitle');
  const { cardCount, rowCount } = getModuleCounts(state);
  const { modules } = shimModules(modulesForShim, {
    basename,
    firstCollectionModuleIndex,
    isAggregationPage: onAggregationPage(data),
    cardCount,
    rowCount,
    data,
    channelCampaignBrandingImage,
    channelSubtitle
  });
  return shimAds(modules, data);
}

/*
 * Types
 */

type ModulesState = Array<Object>;

type ModulesAccumulator = {
  modules: Array<Object>,
  cardCount: number,
  rowCount: number
};

type RowsAccumulator = {
  cardCount: number,
  cardCountWithinModule: number,
  rowCount: number,
  rowCountWithinModule: number,
  rows: Array<Object>
};

type CardsAccumulator = {
  cardCount: number,
  cardCountWithinModule: number,
  cards: Array<Object>
};

type ShimOptions = {
  basename: string,
  rowCount: number,
  cardCount: number,
  isAggregationPage: bool,
  firstCollectionModuleIndex: number
};

type ModuleCounts = {
  cardCount: number,
  rowCount: number,
  cardCountWithinModule: number,
  rowCountWithinModule: number
};
